import React from 'react';
import { connect } from 'react-redux';
import NavBarLayout from '../NavBarLayout';
import { getUserInfo, isUserLogged } from '../../reducers';
import LogoLoading from '../LogoLoading';
import ChallengeGridItem from './challenges/ChallengeGridItem';
import ChallengeDetail from './challenges/ChallengeDetail';
import * as actions from '../../actions/types';
import { getUserChallenges, isUserChallengeActivating } from '../../reducers/user/challenges';
import styled from 'styled-components';
import { breakpoints } from '../../utils/styles';
import RequireAuthMessage from './RequireAuthMessage';
import ChallengeCompletedDetail from './challenges/ChallengeCompletedDetail';
import Submenu from '../common/Submenu';
import Button from '../Button';

const Count = styled.p`
    font-size: 1.2em;
    font-weight: 700;
    margin-top: 1.5rem;
    text-align: center;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        margin-top: 3rem;
    }

    body.theme-market & {
        font-weight: 700;
        text-transform: uppercase;
    }
`;

const ChallengesGrid = styled.div`
    display: grid;
    gap: 1rem;
    margin-bottom: 3rem;
    
    @media screen and (min-width: ${breakpoints.desktop}px) {
        grid-auto-rows: 1fr;
        grid-template-columns: 1fr 1fr;
    }
`;

const ChallengesPage: React.FC = () => {
    return (
        <NavBarLayout
            fixedTitle="Retos"
            routes={[
                {
                    title: 'En curso',
                    component: ConnectedContent,
                    path: '/retos/',
                    showTitleInMobile: false,
                },
                {
                    title: 'Mis logros',
                    render: () => <ConnectedContent completed />,
                    path: '/retos/logros',
                    showTitleInMobile: false,
                },
            ]}
        />
    );
};

export interface Challenge {
    id: number;
    type: string;
    name: string;
    image: {
        traditional: string | null;
        market: string | null;
        platinum: string | null;
    };
    description_short: string;
    description: string;
    completion_text?: string;
    active?: boolean;
    progress: {
        current: number;
        target: number;
    },
    bg_color: {
        traditional: string;
    };
    title_color: {
        traditional: string;
    };
    expiry: string;
}

interface Props {
    challenges: Array<Challenge> | null;
    completed?: boolean;
    isProcessing: boolean;
    isUserLogged: boolean;
    userInfo: { first_name: string; iecode: string | null; };
    activate: (id: number) => void;
    fetch: ({ completed }: { completed: boolean }) => void;
}
const Content: React.FC<Props> = ({ challenges = [], completed = false, isProcessing, isUserLogged, activate, fetch, userInfo }) => {
    const [selectedChallenge, setSelectedChallenge] = React.useState<Challenge | null>(null);

    React.useEffect(() => {
        isUserLogged && fetch({ completed });
    }, [fetch, isUserLogged, completed]);

    if (!isUserLogged) {
        return (
            <RequireAuthMessage icon="fa-medal">
                <p>Para acceder a tus retos, primero debes iniciar sesión.</p>
            </RequireAuthMessage>
        );
    }

    if (!userInfo.iecode) {
        return (
            <RequireAuthMessage icon="fa-medal" buttons={<Button block primary to="/usuario/loop/inscripcion">Registrarme</Button>}>
                <p>Para participar de los retos debes ser miembro Cinemex Loop.</p>
            </RequireAuthMessage>
        );
    }

    const active = challenges ? challenges.filter(challenge => !completed && challenge.active) : [];
    const inactive = challenges ? challenges.filter(challenge => !completed && !challenge.active) : [];
    const done = challenges && completed ? challenges : [];

    return (
        <>
            <p>
                Los retos te permiten obtener beneficios especiales y accesos únicos. ¡Entre más logros acumules, más sorpresas!
            </p>

            {!completed && (
                <p>
                    <strong>¡No te olvides de activar los retos para acumular progreso!</strong>
                </p>
            )}

            <div className='d-block d-md-none'>
                <Submenu items={[
                    { id: 'active', name: 'En curso', to: '/retos/' },
                    { id: 'completed', name: 'Mis logros', to: '/retos/logros' },
                ]} selectedId={completed ? 'completed' : 'active'} />
            </div>

            {done.length > 0 && <ChallenesPanel
                challenges={done}
                completed={true}
                title={`Logros: ${done.length}`}
                setSelectedChallenge={setSelectedChallenge}
                activate={activate}
            />}

            {active.length > 0 && <ChallenesPanel
                challenges={active}
                title={`Retos en curso: ${active.length}`}
                setSelectedChallenge={setSelectedChallenge}
                activate={activate}
            />}

            {inactive.length > 0 && <ChallenesPanel
                challenges={inactive}
                title="¡Activa estos retos para comenzar a progresar en ellos y ganar beneficios increíbles!"
                setSelectedChallenge={setSelectedChallenge}
                activate={activate}
            />}

            {selectedChallenge && (
                completed
                    ? <ChallengeCompletedDetail challenge={selectedChallenge} userFirstName={userInfo.first_name} onClose={() => setSelectedChallenge(null)} />
                    : <ChallengeDetail challenge={selectedChallenge} onClose={() => setSelectedChallenge(null)} />
            )}

            {(null === challenges || isProcessing) && <LogoLoading />}
        </>
    );
};

interface PanelProps {
    challenges: Array<Challenge>;
    completed?: boolean;
    title: string;
    activate: (id: number) => void;
    setSelectedChallenge: (challenge: Challenge) => void;
}

const ChallenesPanel: React.FC<PanelProps> = ({ challenges, completed = false, title, setSelectedChallenge, activate }) => {
    return (
        <>
            <Count>
                {title}
            </Count>

            <ChallengesGrid>
                {challenges.map(challenge => <ChallengeGridItem key={challenge.id}
                    challenge={challenge}
                    completed={completed}
                    onSelect={() => setSelectedChallenge(challenge)}
                    onActivate={activate}
                />)}
            </ChallengesGrid>
        </>
    );
};

const mapStateToProps = (state) => ({
    challenges: getUserChallenges(state),
    isProcessing: isUserChallengeActivating(state),
    isUserLogged: isUserLogged(state),
    userInfo: getUserInfo(state),
});

const mapDispatchToProps = {
    fetch: ({ completed }: { completed: boolean }) => ({ type: actions.USER_CHALLENGES_FETCH, payload: { completed } }),
    activate: (id: number) => ({ type: actions.USER_CHALLENGE_ACTIVATE, payload: { id } }),
};

const ConnectedContent = connect(mapStateToProps, mapDispatchToProps)(Content);

export default ChallengesPage;